import React, { useState } from 'react';
import { Edit2, Trash2, ChevronDown, ChevronUp, MessageSquare } from 'lucide-react';
import { useUpdateGrade, useDeleteGrade } from '../../hooks/useGrading';

const GradebookTable = ({ grades, enrollments, classId, settings }) => {
  const [expandedStudent, setExpandedStudent] = useState(null);
  const [editingGrade, setEditingGrade] = useState(null);

  const updateGrade = useUpdateGrade();
  const deleteGrade = useDeleteGrade();

  // Get grading scale info from settings
  const gradingScale = settings?.gradingScale || 'letter';
  const customScaleConfig = settings?.customScaleConfig || {};
  // eslint-disable-next-line no-unused-vars
  const _customScaleName = settings?.customScaleName || 'Custom Scale';

  // Helper to get grade display based on scale
  const getGradeDisplay = (percentage, letterGrade) => {
    if (gradingScale === 'percentage') {
      return { label: `${percentage}%`, color: '#6b7280' };
    }
    if (gradingScale === 'points') {
      return { label: '-', color: '#6b7280' };
    }
    if (gradingScale === 'pass_fail') {
      const isPass = letterGrade === 'Pass';
      return {
        label: letterGrade || '-',
        color: isPass ? '#22c55e' : '#ef4444'
      };
    }
    if (gradingScale === 'expectations' || gradingScale === 'custom') {
      const level = Object.entries(customScaleConfig).find(([label, config]) => {
        const min = typeof config === 'object' ? config.min : config;
        return percentage >= min;
      });
      if (level) {
        const [label, config] = level;
        const color = typeof config === 'object' ? config.color : '#6b7280';
        return { label, color };
      }
    }
    // Default letter grade
    const grade = letterGrade || '-';
    const colors = {
      'A': '#22c55e',
      'B': '#3b82f6',
      'C': '#eab308',
      'D': '#f97316',
      'F': '#ef4444'
    };
    return { label: grade, color: colors[grade?.[0]] || '#6b7280' };
  };

  // Group grades by student
  const studentGrades = React.useMemo(() => {
    const grouped = {};
    
    // Initialize with enrolled students
    enrollments.forEach(enrollment => {
      const student = enrollment.student;
      if (student) {
        grouped[student.id] = {
          student,
          grades: []
        };
      }
    });

    // Add grades
    grades.forEach(grade => {
      if (grouped[grade.studentId]) {
        grouped[grade.studentId].grades.push(grade);
      }
    });

    return Object.values(grouped);
  }, [grades, enrollments]);

  // Calculate student average
  const getStudentAverage = (studentGrades) => {
    if (!studentGrades.length) return '-';
    const percentages = studentGrades.map(g => g.percentage).filter(Boolean);
    if (!percentages.length) return '-';
    const avg = (percentages.reduce((a, b) => a + b, 0) / percentages.length).toFixed(1);
    return `${avg}%`;
  };

  const getOverallGrade = (studentGrades) => {
    if (!studentGrades.length) return { label: '-', color: '#6b7280' };
    const percentages = studentGrades.map(g => g.percentage).filter(Boolean);
    if (!percentages.length) return { label: '-', color: '#6b7280' };
    const avg = percentages.reduce((a, b) => a + b, 0) / percentages.length;
    return getGradeDisplay(avg, null);
  };

  const handleUpdateGrade = async (gradeId, data) => {
    try {
      await updateGrade.mutateAsync({ gradeId, data });
      setEditingGrade(null);
    } catch (err) {
      console.error('Failed to update grade:', err);
    }
  };

  const handleDeleteGrade = async (gradeId) => {
    if (window.confirm('Are you sure you want to delete this grade?')) {
      try {
        await deleteGrade.mutateAsync(gradeId);
      } catch (err) {
        console.error('Failed to delete grade:', err);
      }
    }
  };

  // Get column header based on scale
  const getGradeColumnHeader = () => {
    switch (gradingScale) {
      case 'expectations':
        return 'Expectation';
      case 'custom':
        return 'Level';
      case 'pass_fail':
        return 'Result';
      case 'percentage':
        return 'Score';
      default:
        return 'Grade';
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-purple-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">Student</th>
            <th className="px-6 py-3 text-center text-xs font-medium text-purple-700 uppercase">Average</th>
            <th className="px-6 py-3 text-center text-xs font-medium text-purple-700 uppercase">{getGradeColumnHeader()}</th>
            <th className="px-6 py-3 text-center text-xs font-medium text-purple-700 uppercase">Assignments</th>
            <th className="px-6 py-3 text-right text-xs font-medium text-purple-700 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {studentGrades.map(({ student, grades: studentGradeList }) => {
            const overallGrade = getOverallGrade(studentGradeList);
            return (
              <React.Fragment key={student.id}>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <button
                        onClick={() => setExpandedStudent(
                          expandedStudent === student.id ? null : student.id
                        )}
                        className="mr-2 text-gray-400 hover:text-gray-600"
                      >
                        {expandedStudent === student.id ? (
                          <ChevronUp size={18} />
                        ) : (
                          <ChevronDown size={18} />
                        )}
                      </button>
                      <div>
                        <p className="font-medium text-gray-900">
                          {student.firstName} {student.lastName}
                        </p>
                        <p className="text-sm text-gray-500">{student.grade}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-lg font-semibold text-gray-900">
                      {getStudentAverage(studentGradeList)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span 
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                      style={{
                        backgroundColor: overallGrade.color + '20',
                        color: overallGrade.color,
                        border: `1px solid ${overallGrade.color}`
                      }}
                    >
                      {overallGrade.label}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center text-gray-600">
                    {studentGradeList.length}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => setExpandedStudent(
                        expandedStudent === student.id ? null : student.id
                      )}
                      className="text-purple-600 hover:text-purple-700 text-sm font-medium"
                    >
                      {expandedStudent === student.id ? 'Hide Details' : 'View Grades'}
                    </button>
                  </td>
                </tr>

                {/* Expanded Grade Details */}
                {expandedStudent === student.id && (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 bg-gray-50">
                      {studentGradeList.length === 0 ? (
                        <p className="text-gray-500 text-center py-4">
                          No grades recorded yet for this student.
                        </p>
                      ) : (
                        <div className="space-y-2">
                          <h4 className="font-medium text-gray-900 mb-3">Grade History</h4>
                          {studentGradeList.map((grade) => {
                            const gradeDisplay = getGradeDisplay(grade.percentage, grade.letterGrade);
                            return (
                              <div 
                                key={grade.id}
                                className="bg-white rounded-lg p-4 border border-gray-200 flex items-center justify-between"
                              >
                                <div className="flex-1">
                                  <div className="flex items-center space-x-4">
                                    <span className="font-medium text-gray-900">
                                      {grade.assignmentName}
                                    </span>
                                    <span className="text-sm text-gray-500">
                                      {grade.gradeCategory}
                                    </span>
                                    <span className="text-sm text-gray-400">
                                      {new Date(grade.gradedDate).toLocaleDateString()}
                                    </span>
                                  </div>
                                  
                                  {editingGrade === grade.id ? (
                                    <div className="mt-2 flex items-center space-x-2">
                                      <input
                                        type="number"
                                        defaultValue={grade.pointsEarned}
                                        className="form-input w-20 text-sm"
                                        id={`edit-points-${grade.id}`}
                                      />
                                      <span className="text-gray-500">/</span>
                                      <span className="text-gray-600">{grade.pointsPossible}</span>
                                      <button
                                        onClick={() => handleUpdateGrade(grade.id, {
                                          pointsEarned: document.getElementById(`edit-points-${grade.id}`).value
                                        })}
                                        className="text-sm text-green-600 hover:text-green-700"
                                      >
                                        Save
                                      </button>
                                      <button
                                        onClick={() => setEditingGrade(null)}
                                        className="text-sm text-gray-500 hover:text-gray-600"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="mt-1 flex items-center space-x-4">
                                      <span className="text-lg font-semibold text-gray-900">
                                        {grade.pointsEarned}/{grade.pointsPossible}
                                      </span>
                                      <span className={`font-medium ${
                                        grade.percentage >= 70 ? 'text-green-600' : 'text-red-600'
                                      }`}>
                                        {grade.percentage}%
                                      </span>
                                      <span 
                                        className="text-sm font-medium px-2 py-0.5 rounded"
                                        style={{
                                          backgroundColor: gradeDisplay.color + '20',
                                          color: gradeDisplay.color
                                        }}
                                      >
                                        {gradeDisplay.label}
                                      </span>
                                      {grade.feedback && (
                                        <span className="flex items-center text-sm text-gray-500">
                                          <MessageSquare size={14} className="mr-1" />
                                          Has feedback
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>

                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => setEditingGrade(grade.id)}
                                    className="p-2 text-gray-400 hover:text-purple-600"
                                    title="Edit"
                                  >
                                    <Edit2 size={16} />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteGrade(grade.id)}
                                    className="p-2 text-gray-400 hover:text-red-600"
                                    title="Delete"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}

          {studentGrades.length === 0 && (
            <tr>
              <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                No students enrolled in this class.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default GradebookTable;
